const toStr = (v) => String(v?._id ?? v?.$oid ?? v ?? "").trim();

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

export function isObjectIdString(v) {
  return OBJECT_ID_RE.test(toStr(v));
}

/**
 * Build shareable selectedResolved rows from a favorite, order item, or taste payload.
 * Accepts selectedResolved, a raw rows array, or a selected map of groupId → optionIds.
 */
export function selectedResolvedFromSelections(selections) {
  if (!selections) return [];
  const source = Array.isArray(selections)
    ? selections
    : Array.isArray(selections.selectedResolved)
      ? selections.selectedResolved
      : [];
  const fromResolved = [];
  for (const row of source) {
    const groupId = toStr(row?.groupId);
    const optionId = toStr(row?.optionId);
    if (!isObjectIdString(groupId) || !isObjectIdString(optionId)) continue;
    fromResolved.push({
      groupId,
      groupKey: row?.groupKey || "",
      groupName: row?.groupName || "",
      optionId,
      optionKey: row?.optionKey || "",
      optionName: row?.optionName || "",
      qty: Math.max(1, Number(row?.qty) || 1),
      ...(row?.pricing ? { pricing: row.pricing } : {}),
      ...(row?.unitPrice != null ? { unitPrice: row.unitPrice } : {}),
    });
  }
  if (fromResolved.length) return fromResolved;

  const selected = selections.selected;
  if (!selected || typeof selected !== "object" || Array.isArray(selected)) return [];
  const rows = [];
  for (const [gid, picked] of Object.entries(selected)) {
    const groupId = toStr(gid);
    if (!isObjectIdString(groupId)) continue;
    const ids = Array.isArray(picked) ? picked : picked ? [picked] : [];
    const counts = new Map();
    for (const oid of ids) {
      const optionId = toStr(oid);
      if (!isObjectIdString(optionId)) continue;
      counts.set(optionId, (counts.get(optionId) || 0) + 1);
    }
    for (const [optionId, qty] of counts) {
      rows.push({ groupId, optionId, qty });
    }
  }
  return rows;
}

export function shareableOrderItems(order) {
  const items = Array.isArray(order?.items) ? order.items : [];
  return items.filter((item) => {
    if (!isObjectIdString(item?.menuItemId?._id || item?.menuItemId)) return false;
    return selectedResolvedFromSelections(item?.selectedResolved || item?.selections).length > 0;
  });
}

export function firstShareableOrderItem(order) {
  return shareableOrderItems(order)[0] || null;
}

export function formatEuroAmount(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || !(v > 0)) return "";
  return `€${v.toFixed(2)}`;
}

export function tasteDisplayPrice(taste) {
  const total = Number(taste?.totalPrice);
  if (Number.isFinite(total) && total > 0) return formatEuroAmount(total);
  return formatEuroAmount(taste?.itemPrice);
}

export function formatAddonPricePart(row) {
  if (String(row?.pricing || "").toLowerCase() !== "addon") return "";
  const unit = Math.max(0, Number(row?.unitPrice) || 0);
  const qty = Math.max(1, Number(row?.qty) || 1);
  const amount = unit * qty;
  if (!(amount > 0)) return "";
  return ` +€${amount.toFixed(2)}`;
}

const FEED_GROUP_ALIASES = {
  vinades: "Viandes",
  viande: "Viandes",
  viandes: "Viandes",
  supplements: "Suppléments",
  supplement: "Supplément",
};

const FEED_OPTION_ALIASES = {
  one: "One",
};

export function humanizeFeedGroupName(raw) {
  const cleaned = String(raw || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "Options";
  const key = cleaned.toLowerCase();
  if (FEED_GROUP_ALIASES[key]) return FEED_GROUP_ALIASES[key];
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function humanizeFeedOptionName(raw) {
  const cleaned = String(raw || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  const key = cleaned.toLowerCase();
  if (FEED_OPTION_ALIASES[key]) return FEED_OPTION_ALIASES[key];
  return cleaned;
}

export function formatConfigurationOptionLabel(optionName, qty = 1, pricing, unitPrice) {
  const option = humanizeFeedOptionName(optionName);
  if (!option) return "";
  const n = Number(qty);
  const qtyPart = Number.isFinite(n) && n > 1 ? ` ×${Math.floor(n)}` : "";
  const pricePart = formatAddonPricePart({
    pricing,
    unitPrice,
    qty: Number.isFinite(n) && n > 0 ? n : 1,
  });
  return `${option}${qtyPart}${pricePart}`;
}

function parseSummaryLine(line) {
  const raw = String(line || "").trim();
  if (!raw) return null;
  const idx = raw.indexOf(":");
  if (idx === -1) return { groupName: "Options", optionName: raw };
  return {
    groupName: raw.slice(0, idx).trim(),
    optionName: raw.slice(idx + 1).trim(),
  };
}

export function tasteConfigurationGroups(taste) {
  const rows = [];
  if (Array.isArray(taste?.summaryPicks) && taste.summaryPicks.length) {
    for (const pick of taste.summaryPicks) {
      rows.push({
        groupName: pick?.groupName,
        optionName: pick?.optionName,
        qty: pick?.qty,
        pricing: pick?.pricing,
        unitPrice: pick?.unitPrice,
        label: pick?.label,
      });
    }
  } else if (Array.isArray(taste?.selections) && taste.selections.length) {
    for (const row of taste.selections) rows.push(row);
  } else if (Array.isArray(taste?.summaryLines)) {
    for (const line of taste.summaryLines) {
      const parsed = parseSummaryLine(line);
      if (parsed) rows.push(parsed);
    }
  }

  const byGroup = new Map();
  const order = [];
  for (const row of rows) {
    const groupName = humanizeFeedGroupName(row?.groupName || "Options");
    let optionLabel = "";
    if (row?.optionName) {
      optionLabel = formatConfigurationOptionLabel(
        row.optionName,
        row?.qty,
        row?.pricing,
        row?.unitPrice
      );
    } else {
      const given = String(row?.label || "").trim();
      if (given.includes(":")) {
        const parsed = parseSummaryLine(given);
        optionLabel = parsed?.optionName || given;
      } else {
        optionLabel = given;
      }
    }
    if (!optionLabel) continue;
    if (!byGroup.has(groupName)) {
      byGroup.set(groupName, []);
      order.push(groupName);
    }
    byGroup.get(groupName).push(optionLabel);
  }

  return order.map((groupName) => ({
    groupName,
    options: byGroup.get(groupName),
    optionsText: byGroup.get(groupName).join(" · "),
  }));
}

export function formatConfigurationGroupInline(group) {
  const name = String(group?.groupName || "").trim();
  const options = String(group?.optionsText || group?.options?.join(" · ") || "").trim();
  if (!name && !options) return "";
  if (!options) return `${name} :`;
  return `${name} : ${options}`;
}

export function tasteConfigurationInline(taste, { limit } = {}) {
  const groups = tasteConfigurationGroups(taste);
  const slice = Number.isFinite(limit) && limit > 0 ? groups.slice(0, limit) : groups;
  return slice.map(formatConfigurationGroupInline).filter(Boolean).join(", ");
}

function groupOptionCounts(rows) {
  const byGroup = new Map();
  for (const row of rows || []) {
    const groupId = toStr(row?.groupId);
    const optionId = toStr(row?.optionId);
    if (!groupId || !optionId) continue;
    if (!byGroup.has(groupId)) byGroup.set(groupId, new Map());
    const opts = byGroup.get(groupId);
    const qty = Math.max(1, Math.floor(Number(row?.qty) || 1));
    opts.set(optionId, (opts.get(optionId) || 0) + qty);
  }
  return byGroup;
}

function groupOptionCountsFromSelectedMap(selected) {
  const rows = [];
  if (!selected || typeof selected !== "object") return groupOptionCounts(rows);
  for (const [groupId, picked] of Object.entries(selected)) {
    if (!Array.isArray(picked)) continue;
    for (const optionId of picked) {
      rows.push({ groupId, optionId, qty: 1 });
    }
  }
  return groupOptionCounts(rows);
}

function serializeCombinationCounts(byGroup) {
  const groupIds = [...byGroup.keys()].sort();
  const parts = [];
  for (const groupId of groupIds) {
    const opts = byGroup.get(groupId);
    const optionParts = [...opts.keys()]
      .sort()
      .map((optionId) => `${optionId}:${opts.get(optionId)}`);
    if (!optionParts.length) continue;
    parts.push(`${groupId}=${optionParts.join(",")}`);
  }
  return parts.join("|");
}

/** Mirrors backend buildCombinationKeyFromResolved / buildCombinationKeyFromSelectedMap. */
export function combinationKeyFromTaste(taste) {
  const direct = toStr(taste?.combinationKey);
  if (direct) return direct;
  if (taste?.selected && typeof taste.selected === "object") {
    return serializeCombinationCounts(groupOptionCountsFromSelectedMap(taste.selected));
  }
  if (Array.isArray(taste?.selections) && taste.selections.length) {
    return serializeCombinationCounts(groupOptionCounts(taste.selections));
  }
  return "";
}

export function combinationGroupKey(taste) {
  const menuItemId = toStr(taste?.menuItemId);
  const combo = combinationKeyFromTaste(taste);
  if (!menuItemId || !combo) return "";
  return `${menuItemId}:${combo}`;
}

export function tasteAuthorUserId(taste) {
  return toStr(taste?.userId);
}

export function tasteAuthorRefCode(taste) {
  return toStr(taste?.author?.userRefCode).toUpperCase();
}

export function tastePeerIdentity(taste) {
  const userId = tasteAuthorUserId(taste);
  if (userId) return `user:${userId}`;
  const refCode = tasteAuthorRefCode(taste);
  if (refCode) return `ref:${refCode}`;
  const postId = toStr(taste?._id);
  return postId ? `post:${postId}` : "";
}

export function dedupeCombinationPeersByUser(tastes, { excludeIdentity } = {}) {
  const seen = new Set();
  const out = [];
  for (const row of tastes || []) {
    const identity = tastePeerIdentity(row);
    if (!identity || seen.has(identity)) continue;
    if (excludeIdentity && identity === excludeIdentity) continue;
    seen.add(identity);
    out.push(row);
  }
  return out;
}

export function combinationSiblingTastes(taste, allTastes) {
  const key = combinationGroupKey(taste);
  if (!key) return [];
  const selfIdentity = tastePeerIdentity(taste);
  const selfPostId = toStr(taste?._id);
  const matches = (allTastes || []).filter((row) => {
    if (toStr(row?._id) === selfPostId) return false;
    if (combinationGroupKey(row) !== key) return false;
    if (selfIdentity && tastePeerIdentity(row) === selfIdentity) return false;
    return true;
  });
  return dedupeCombinationPeersByUser(matches);
}

/** @deprecated Feed shows individual cards; use combinationSiblingTastes instead. */
export function groupFeedTastesForDisplay(tastes) {
  const comboBuckets = new Map();
  for (const taste of tastes || []) {
    const key = combinationGroupKey(taste);
    if (!key) continue;
    if (!comboBuckets.has(key)) comboBuckets.set(key, []);
    comboBuckets.get(key).push(taste);
  }

  const entries = [];
  const emittedCombo = new Set();

  for (const taste of tastes || []) {
    const key = combinationGroupKey(taste);
    const bucket = key ? comboBuckets.get(key) : null;
    if (!key || !bucket || bucket.length < 2) {
      entries.push({ kind: "post", taste });
      continue;
    }
    if (emittedCombo.has(key)) continue;
    emittedCombo.add(key);
    entries.push({
      kind: "combination",
      combinationKey: key,
      tastes: bucket,
      taste: bucket[0],
    });
  }

  return entries;
}

export function selectionSummaryFromItem(item) {
  const rows = selectedResolvedFromSelections(item?.selectedResolved || item?.selections);
  if (!rows.length) return { summaryLines: [], text: "" };
  const byGroup = {};
  for (const row of rows) {
    const group = row.groupName || "Options";
    if (!byGroup[group]) byGroup[group] = [];
    const qty = Number(row.qty) || 1;
    const label = row.optionName || "Option";
    const qtyLabel = qty > 1 ? `${label} ×${qty}` : label;
    byGroup[group].push(`${qtyLabel}${formatAddonPricePart(row)}`);
  }
  const summaryLines = Object.entries(byGroup).map(
    ([group, opts]) => `${group}: ${[...new Set(opts)].join(", ")}`
  );
  return { summaryLines, text: summaryLines.join(" · ") };
}

export function sharePathFromOrder(orderId) {
  const id = toStr(orderId);
  return id ? `/share-taste/${id}` : "/feed";
}

/**
 * Map a taste API payload into NewSelectionPage `selected` state:
 * { [groupId]: [optionId, optionId, ...] }
 */
export function selectedMapFromTaste(taste) {
  if (!taste || typeof taste !== "object") return {};
  if (taste.selected && typeof taste.selected === "object" && !Array.isArray(taste.selected)) {
    const next = {};
    for (const [gid, picked] of Object.entries(taste.selected)) {
      if (!Array.isArray(picked) || !picked.length) continue;
      next[toStr(gid)] = picked.map(toStr).filter(Boolean);
    }
    return next;
  }
  const rows = Array.isArray(taste.selections) ? taste.selections : [];
  const next = {};
  for (const row of rows) {
    const gid = toStr(row?.groupId);
    const oid = toStr(row?.optionId);
    if (!gid || !oid) continue;
    const qty = Math.max(1, Number(row?.qty) || 1);
    if (!next[gid]) next[gid] = [];
    for (let i = 0; i < qty; i += 1) next[gid].push(oid);
  }
  return next;
}

export function tasteQueryFromSearch(searchParams) {
  if (!searchParams) return "";
  const raw =
    typeof searchParams.get === "function"
      ? searchParams.get("taste")
      : searchParams.taste;
  return toStr(raw);
}

export function newSelectionPath(
  menuItemId,
  { tasteId, comments, redeem, share, orderId, askOrderType, discovery, discoveryCategory } = {}
) {
  const id = toStr(menuItemId);
  if (!id) return "/new-selection";
  const qs = new URLSearchParams();
  if (tasteId) qs.set("taste", toStr(tasteId));
  if (comments) qs.set("comments", "1");
  if (redeem) qs.set("redeem", "1");
  if (share) qs.set("share", "1");
  if (orderId && isObjectIdString(orderId)) qs.set("order", toStr(orderId));
  if (askOrderType) qs.set("askOrderType", "1");
  if (discovery) qs.set("discovery", "1");
  if (discoveryCategory) qs.set("discoveryCategory", String(discoveryCategory).slice(0, 40));
  const q = qs.toString();
  return q ? `/new-selection/${id}?${q}` : `/new-selection/${id}`;
}

export function restaurantTastesPath(restaurantId) {
  const id = toStr(restaurantId);
  return id ? `/restaurant/${id}` : "/explore";
}

/** Customer menu page scrolled to the restaurant offers section. */
export function restaurantOffersPath(restaurantId) {
  const id = toStr(restaurantId);
  return id ? `/restaurant/${id}?section=offers` : "/explore";
}

export function userProfilePath(userRefCode) {
  const code = toStr(userRefCode);
  if (!code) return "/explore";
  return `/u/${encodeURIComponent(code)}`;
}

export function userProfileStatsPath(userRefCode) {
  const profile = userProfilePath(userRefCode);
  return profile === "/explore" ? profile : `${profile}/stats`;
}

export function feedPath() {
  return "/feed";
}

export function tastePostPath(tasteId) {
  const id = toStr(tasteId);
  return id ? `/t/${encodeURIComponent(id)}` : feedPath();
}

export const COMBINATION_PEER_PREVIEW = 3;

export function combinationTriedPath(postId) {
  const id = toStr(postId);
  return id ? `/feed/combination/${encodeURIComponent(id)}` : feedPath();
}

export function userProfilePostPath(userRefCode, tasteId) {
  const profile = userProfilePath(userRefCode);
  const id = toStr(tasteId);
  if (!id || profile === "/explore") return profile;
  return `${profile}?post=${encodeURIComponent(id)}`;
}

export function absoluteAppUrl(path) {
  const raw = String(path || "").trim();
  const normalized = raw.startsWith("/") ? raw : raw ? `/${raw}` : "/";
  if (typeof window === "undefined" || !window.location?.origin) return normalized;
  return `${window.location.origin}${normalized}`;
}

export function postIdFromSearch(searchParams) {
  if (!searchParams) return "";
  const raw =
    typeof searchParams.get === "function" ? searchParams.get("post") : searchParams.post;
  return toStr(raw);
}

export function menuItemSharePayload(t, { menuItemId, itemName, restaurantName } = {}) {
  const item = String(itemName || "").trim() || (typeof t === "function" ? t("newSelection.item") : "Item");
  const restaurant = String(restaurantName || "").trim();
  const translate = typeof t === "function" ? t : (key) => key;
  return {
    title: restaurant
      ? translate("restaurantMenu.shareItemTitle", { item, restaurant })
      : item,
    text: restaurant
      ? translate("restaurantMenu.shareItemText", { item, restaurant })
      : translate("restaurantMenu.shareItemTextNoRestaurant", { item }),
    url: absoluteAppUrl(newSelectionPath(menuItemId)),
  };
}

export function postableItemsFromOrder(order) {
  if (!order) return [];
  return shareableOrderItems(order).map((item, i) => {
    const resolved = selectedResolvedFromSelections(item?.selectedResolved || item?.selections);
    const { summaryLines } = selectionSummaryFromItem(item);
    return {
      key: `${toStr(order._id)}:${i}:${toStr(item?.menuItemId)}`,
      orderId: toStr(order._id),
      restaurantId: toStr(order.restaurantId),
      restaurantName: order.restaurantName || "",
      categoryType: order.categoryType || item?.categoryType,
      menuItemId: toStr(item?.menuItemId?._id || item?.menuItemId),
      name: item?.name || "",
      image: "",
      itemPrice: Math.max(0, Number(item?.basePrice) || 0),
      totalPrice:
        Math.max(0, Number(item?.basePrice) || 0) +
        resolved.reduce((sum, row) => {
          if (String(row?.pricing || "").toLowerCase() !== "addon") return sum;
          return sum + Math.max(0, Number(row?.unitPrice) || 0) * Math.max(1, Number(row?.qty) || 1);
        }, 0),
      selected: item?.selections?.selected,
      selectedResolved: resolved,
      summaryLines,
    };
  });
}

/** Profile status ring is live until statusExpiresAt. A feed post stays after that. */
export function tasteStatusIsActive(taste, now = Date.now()) {
  if (!taste) return false;
  const raw =
    taste.statusExpiresAt || (taste.visibility === "profile" ? taste.expiresAt : null);
  if (!raw) return false;
  const at = new Date(raw).getTime();
  return Number.isFinite(at) && at > now;
}

/**
 * Active profile statuses in Instagram order (oldest → newest).
 * Multiple statuses in a day are kept; the ring plays through all of them.
 */
export function activeTasteStatuses(tastes, now = Date.now()) {
  const list = (Array.isArray(tastes) ? tastes : []).filter((row) => tasteStatusIsActive(row, now));
  if (list.length < 2) return list;
  return [...list].sort((a, b) => {
    const byDate = tasteCreatedAtMs(a) - tasteCreatedAtMs(b);
    if (byDate !== 0) return byDate;
    return toStr(a?._id).localeCompare(toStr(b?._id));
  });
}

/** Map taste statuses to StoryViewer items (`id`, `mediaUrl`, `caption`, `createdAt`, `menuItemId`). */
export function tasteStatusesToStories(tastes) {
  return (Array.isArray(tastes) ? tastes : [])
    .map((taste) => {
      const mediaUrl = tasteDisplayPhoto(taste);
      if (!mediaUrl) return null;
      const menuItemId = toStr(taste?.menuItemId);
      return {
        id: toStr(taste?._id),
        mediaUrl,
        caption: String(taste?.caption || "").trim(),
        createdAt: taste?.createdAt || null,
        ...(menuItemId ? { menuItemId } : {}),
        restaurantName: String(taste?.restaurantName || "").trim(),
        menuItemName: String(taste?.menuItemName || taste?.name || "").trim(),
      };
    })
    .filter(Boolean);
}

export function shareTastePayload({
  restaurantId,
  menuItemId,
  categoryType,
  name,
  selected,
  selectedResolved,
  photoUrl,
  photoPublicId,
  caption,
  fromOrderId,
  orderItemId,
  orderItemIndex,
  visibility,
  asStatus,
}) {
  const resolved = selectedResolvedFromSelections({
    selected,
    selectedResolved: Array.isArray(selectedResolved) ? selectedResolved : [],
  });
  const vis = String(visibility || "").trim().toLowerCase();
  const comment = String(caption || "").trim().slice(0, 500);
  return {
    restaurantId: toStr(restaurantId),
    menuItemId: toStr(menuItemId),
    categoryType: categoryType || "sandwich",
    ...(name ? { name: String(name).trim().slice(0, 80) } : {}),
    ...(photoUrl ? { photoUrl: String(photoUrl).trim() } : {}),
    ...(photoPublicId ? { photoPublicId: String(photoPublicId).trim() } : {}),
    ...(comment ? { caption: comment } : {}),
    ...(fromOrderId && isObjectIdString(fromOrderId) ? { fromOrderId: toStr(fromOrderId) } : {}),
    ...(orderItemId ? { orderItemId: toStr(orderItemId) } : {}),
    ...(orderItemIndex != null && orderItemIndex !== "" ? { orderItemIndex: Number(orderItemIndex) } : {}),
    ...(vis === "profile" || vis === "feed" ? { visibility: vis } : {}),
    ...(asStatus || vis === "profile" ? { asStatus: true } : {}),
    selections: {
      selected: selected && typeof selected === "object" ? selected : {},
      selectedResolved: resolved,
    },
  };
}

export function feedPostPayload(args) {
  const base = shareTastePayload(args);
  const { selections, ...rest } = base;
  return rest;
}

export function itemFeedPath(menuItemId) {
  const id = toStr(menuItemId);
  return id ? `/feed/item/${id}` : "/feed";
}

export function itemFeedLinkLabel(t, count) {
  const people = Math.floor(Number(count));
  if (Number.isFinite(people) && people > 0) {
    return t("tastes.itemFeedLinkCount", {
      count: people,
      defaultValue:
        people === 1
          ? "See what {{count}} person posted about this item"
          : "See what {{count}} people posted about this item",
    });
  }
  return t("tastes.itemFeedLink", { defaultValue: "See what people posted about this item" });
}

export function shareRewardSuccessMessage(t, { visibility, pointsAwarded, restaurantPointsAwarded } = {}) {
  const platform = Math.max(0, Number(pointsAwarded) || 0);
  const restaurant = Math.max(0, Number(restaurantPointsAwarded) || 0);
  if (platform > 0 && restaurant > 0) {
    return t("tastes.postedFeedBothPointsToast", { platform, restaurant });
  }
  if (restaurant > 0) return t("tastes.postedFeedRestaurantPointsToast", { points: restaurant });
  if (platform > 0) return t("tastes.postedFeedPointsToast", { points: platform });
  if (visibility === "feed") return t("tastes.postedFeedToast");
  return t("tastes.postedProfileToast");
}

export function unshareRewardSuccessMessage(t, { pointsReversed, restaurantPointsReversed } = {}) {
  const platform = Math.max(0, Number(pointsReversed) || 0);
  const restaurant = Math.max(0, Number(restaurantPointsReversed) || 0);
  if (platform > 0 && restaurant > 0) {
    return t("tastes.unsharedWithBothPoints", { platform, restaurant });
  }
  if (restaurant > 0) return t("tastes.unsharedWithRestaurantPoints", { points: restaurant });
  if (platform > 0) return t("tastes.unsharedWithPoints", { points: platform });
  return t("tastes.unshared");
}

export function deleteAllRewardSuccessMessage(t, { pointsReversed, restaurantPointsReversed } = {}) {
  const platform = Math.max(0, Number(pointsReversed) || 0);
  const restaurant = Math.max(0, Number(restaurantPointsReversed) || 0);
  if (platform > 0 && restaurant > 0) {
    return t("tastes.deletedAllWithBothPoints", { platform, restaurant });
  }
  if (restaurant > 0) return t("tastes.deletedAllWithRestaurantPoints", { points: restaurant });
  if (platform > 0) return t("tastes.deletedAllWithPoints", { points: platform });
  return t("tastes.deletedAll");
}

export const TASTE_PICK_PREVIEW = 3;

export function formatGroupOptionLabel(groupName, optionName, qty = 1, price = null) {
  const group = String(groupName || "").replace(/\s+/g, " ").trim();
  const option = String(optionName || "").replace(/\s+/g, " ").trim();
  const n = Number(qty);
  const qtyPart = Number.isFinite(n) && n > 1 ? ` ×${Math.floor(n)}` : "";
  const pricePart = formatAddonPricePart({
    pricing: price?.pricing,
    unitPrice: price?.unitPrice,
    qty: Number.isFinite(n) && n > 0 ? n : 1,
  });
  if (group && option) return `${group} ${option}${qtyPart}${pricePart}`;
  return `${option || group}${qtyPart}${pricePart}`;
}

export function tastePickLabels(taste) {
  if (Array.isArray(taste?.summaryPicks) && taste.summaryPicks.length) {
    return taste.summaryPicks
      .map((p) => {
        const given = String(p?.label || "").trim();
        if (given) return given;
        return formatGroupOptionLabel(p?.groupName, p?.optionName, p?.qty, p);
      })
      .filter(Boolean);
  }
  const lines = Array.isArray(taste?.summaryLines) ? taste.summaryLines.map((s) => String(s || "").trim()).filter(Boolean) : [];
  return lines;
}

export function tasteDisplayPhoto(taste) {
  return String(
    taste?.photoUrl ||
      taste?.menuItemImage ||
      taste?.offerSnapshot?.image ||
      taste?.restaurantImage ||
      ""
  ).trim();
}

/** Square grid tile. The opened post still uses the original photo. */
export function profileGridPhoto(url) {
  const raw = String(url || "").trim();
  const marker = "/image/upload/";
  const at = raw.indexOf(marker);
  if (at < 0) return raw;
  const rest = raw.slice(at + marker.length);
  const first = rest.split("/")[0] || "";
  if (first.includes(",") || /^(c_|w_|h_|f_|q_|g_|dpr_|e_)/.test(first)) return raw;
  return `${raw.slice(0, at)}${marker}c_fill,g_auto,w_480,h_480,f_auto,q_auto/${rest}`;
}

export function groupTastesByMenuItem(tastes) {
  const groups = [];
  const map = new Map();
  for (const taste of tastes || []) {
    const mid = toStr(taste?.menuItemId);
    if (!mid) continue;
    if (!map.has(mid)) {
      const group = {
        menuItemId: mid,
        menuItemName: taste.menuItemName || "",
        tastes: [],
      };
      map.set(mid, group);
      groups.push(group);
    }
    map.get(mid).tastes.push(taste);
  }
  return groups;
}

export function tasteBelongsToItem(taste, menuItemId) {
  if (!taste) return false;
  return toStr(taste.menuItemId) === toStr(menuItemId);
}

export function uniqueFeedRestaurants(tastes) {
  const list = [];
  const seen = new Set();
  for (const taste of tastes || []) {
    const restaurantId = toStr(taste?.restaurantId);
    if (!restaurantId || seen.has(restaurantId)) continue;
    seen.add(restaurantId);
    list.push({
      restaurantId,
      restaurantName: String(taste.restaurantName || "").trim(),
    });
  }
  return list;
}

export function uniqueFeedMenuItems(tastes, restaurantId) {
  const allowed = new Set(
    (Array.isArray(restaurantId) ? restaurantId : restaurantId ? [restaurantId] : [])
      .map(toStr)
      .filter(Boolean)
  );
  const list = [];
  const seen = new Set();
  for (const taste of tastes || []) {
    if (allowed.size && !allowed.has(toStr(taste?.restaurantId))) continue;
    const menuItemId = toStr(taste?.menuItemId);
    if (!menuItemId || seen.has(menuItemId)) continue;
    seen.add(menuItemId);
    list.push({
      menuItemId,
      menuItemName: String(taste.menuItemName || "").trim(),
      restaurantId: toStr(taste.restaurantId),
    });
  }
  return list;
}

export function dishNameKey(name) {
  return String(name || "").trim().toLowerCase();
}

export function dishNamesMatch(a, b) {
  const left = dishNameKey(a);
  const right = dishNameKey(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

export function uniqueFeedDishNames(tastes, restaurantIds) {
  const allowed = new Set((restaurantIds || []).map(toStr).filter(Boolean));
  const list = [];
  const seen = new Set();
  for (const taste of tastes || []) {
    if (allowed.size && !allowed.has(toStr(taste?.restaurantId))) continue;
    const menuItemName = String(taste?.menuItemName || "").trim();
    const key = dishNameKey(menuItemName);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push({ dishKey: key, menuItemName });
  }
  return list;
}

export function normalizeFeedQuery(query) {
  return String(query || "").trim().toLowerCase();
}

export function tasteMatchesFeedQuery(taste, query) {
  const q = normalizeFeedQuery(query);
  if (!q) return true;
  const hay = [taste?.restaurantName, taste?.menuItemName, taste?.name, taste?.caption]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
  return hay.includes(q);
}

export function optionMatchesFeedQuery(option, query, keys = []) {
  const q = normalizeFeedQuery(query);
  if (!q) return true;
  return keys.some((key) => String(option?.[key] || "").toLowerCase().includes(q));
}

export function tasteCreatedAtMs(taste) {
  const raw = taste?.createdAt;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function compareTastesByRecent(a, b) {
  const byDate = tasteCreatedAtMs(b) - tasteCreatedAtMs(a);
  if (byDate !== 0) return byDate;
  return toStr(b?._id).localeCompare(toStr(a?._id));
}

export function sortTastesByRecent(tastes) {
  if (!Array.isArray(tastes) || tastes.length < 2) return Array.isArray(tastes) ? tastes : [];
  return [...tastes].sort(compareTastesByRecent);
}

export function filterFeedTastes(tastes, { restaurantId, restaurantIds, menuItemId, dishKeys, query } = {}) {
  const rids = new Set(
    (restaurantIds || (restaurantId ? [restaurantId] : [])).map(toStr).filter(Boolean)
  );
  const keys = (dishKeys || []).map(dishNameKey).filter(Boolean);
  const mid = toStr(menuItemId);
  return (tastes || []).filter((taste) => {
    if (rids.size && !rids.has(toStr(taste?.restaurantId))) return false;
    if (mid && toStr(taste?.menuItemId) !== mid) return false;
    if (keys.length && !keys.some((key) => dishNamesMatch(taste?.menuItemName, key))) return false;
    return tasteMatchesFeedQuery(taste, query);
  });
}

function splitFilterValues(raw) {
  return String(raw || "")
    .split(",")
    .map((v) => toStr(v))
    .filter(Boolean);
}

export function feedFilterFromSearch(searchParams) {
  if (!searchParams) return { restaurantIds: [], dishKeys: [], restaurantId: "", menuItemId: "" };
  const read =
    typeof searchParams.get === "function"
      ? (key) => searchParams.get(key)
      : (key) => searchParams[key];
  const restaurantIds = splitFilterValues(read("restaurant"));
  const dishKeys = splitFilterValues(read("dish") || read("item")).map(dishNameKey);
  return {
    restaurantIds,
    dishKeys,
    restaurantId: restaurantIds[0] || "",
    menuItemId: toStr(read("item")),
  };
}

function authorRefCode(taste) {
  return String(taste?.author?.userRefCode || "").trim().toUpperCase();
}

function isRestaurantAuthorPost(taste) {
  const author = taste?.author || {};
  return (
    taste?.postSource === "owner_menu" ||
    taste?.postSource === "owner_promo" ||
    taste?.authorType === "owner" ||
    author.authorType === "owner" ||
    author.isOfficial === true
  );
}

export function shareFollowFlags(tastes) {
  if (!Array.isArray(tastes) || tastes.length < 2) return Array.isArray(tastes) ? tastes : [];
  const followedPeople = new Set();
  const followedRestaurants = new Set();
  for (const row of tastes) {
    if (row?.author?.myFollowing !== true) continue;
    if (isRestaurantAuthorPost(row)) {
      const restaurantId = String(row.restaurantId || "").trim();
      if (restaurantId) followedRestaurants.add(restaurantId);
    } else {
      const ref = authorRefCode(row);
      if (ref) followedPeople.add(ref);
    }
  }
  if (!followedPeople.size && !followedRestaurants.size) return tastes;
  return tastes.map((row) => {
    if (row?.author?.myFollowing === true) return row;
    const followed = isRestaurantAuthorPost(row)
      ? followedRestaurants.has(String(row?.restaurantId || "").trim())
      : followedPeople.has(authorRefCode(row));
    if (!followed || !row?.author) return row;
    return { ...row, author: { ...row.author, myFollowing: true } };
  });
}

export function applyTasteChange(list, next) {
  if (!Array.isArray(list)) return [];
  if (!next) return list;
  const previous = list.find((row) => String(row?._id) === String(next._id));
  const followChanged =
    Boolean(previous) &&
    Boolean(previous?.author?.myFollowing) !== Boolean(next?.author?.myFollowing);
  return list.map((row) => {
    if (String(row?._id) === String(next._id)) return next;
    if (!followChanged || !row?.author) return row;
    if (isRestaurantAuthorPost(next) && isRestaurantAuthorPost(row)) {
      const restaurantId = String(next.restaurantId || "").trim();
      if (restaurantId && restaurantId === String(row.restaurantId || "").trim()) {
        return { ...row, author: { ...row.author, myFollowing: Boolean(next.author?.myFollowing) } };
      }
      return row;
    }
    const ref = authorRefCode(next);
    if (ref && ref === authorRefCode(row)) {
      return { ...row, author: { ...row.author, myFollowing: Boolean(next.author?.myFollowing) } };
    }
    return row;
  });
}

export function feedPathWithFilters({ restaurantId, restaurantIds, menuItemId, dishKeys } = {}) {
  const qs = new URLSearchParams();
  const rids = (restaurantIds || (restaurantId ? [restaurantId] : [])).map(toStr).filter(Boolean);
  const keys = (dishKeys || []).map(dishNameKey).filter(Boolean);
  if (rids.length) qs.set("restaurant", rids.join(","));
  if (keys.length) qs.set("dish", keys.join(","));
  else if (menuItemId) qs.set("item", toStr(menuItemId));
  const q = qs.toString();
  return q ? `${feedPath()}?${q}` : feedPath();
}
